import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';

import { ErrorBoundary } from './ErrorBoundary';
import { AuthenticatedShell } from './AuthenticatedShell';
import { LoadingScreen } from '@/components/LoadingScreen';
import { useSession } from '@/features/auth/session';
import { LoginPage } from '@/pages/LoginPage';
import { MessengerPage } from '@/pages/MessengerPage';
import { RegisterPage } from '@/pages/RegisterPage';

/**
 * Heavier screens load on demand.
 *
 * Settings, the profile editor and the call surface pull in code most sessions
 * never open, and the first paint that matters is the chat list.
 */
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((module) => ({ default: module.NotFoundPage })),
);

function RequireAnonymous() {
  const { status } = useSession();
  if (status === 'loading') return <LoadingScreen label="Проверяем сессию" />;
  if (status === 'authenticated') return <Navigate to="/chats" replace />;
  return <Outlet />;
}

function RequireSession() {
  const { status } = useSession();
  // Waiting is deliberate: bouncing to sign-in while the refresh call is still
  // in flight would log people out on every reload.
  if (status === 'loading') return <LoadingScreen label="Проверяем сессию" />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return (
    <AuthenticatedShell>
      <Outlet />
    </AuthenticatedShell>
  );
}

const router = createBrowserRouter([
  {
    element: <RequireAnonymous />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
    ],
  },
  {
    element: <RequireSession />,
    children: [
      { path: '/', element: <Navigate to="/chats" replace /> },
      { path: '/chats', element: <MessengerPage /> },
      { path: '/chats/:chatId', element: <MessengerPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/:section', element: <SettingsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);

export function AppRouter() {
  return (
    <ErrorBoundary area="router">
      <Suspense fallback={<LoadingScreen label="Загрузка" />}>
        <RouterProvider router={router} />
      </Suspense>
    </ErrorBoundary>
  );
}
